import { isolatedDesignWindow, loadDesignExportFrame } from "@/features/design/isolated-preview";
import { authenticatedFetch, operation } from "@/lib/api-client";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent, type DragEvent } from "react";
import {
  ArrowLeft,
  Send,
  Loader2,
  Download,
  FileText,
  FolderTree,
  Palette,
  Sparkles,
  AlertCircle,
  X,
  Paperclip,
  Wrench,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AssetsPanel } from "@/components/design/assets-panel";
import { FilesPanel } from "@/components/design/files-panel";
import { InputsPanel } from "@/components/design/inputs-panel";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import { aws as awsClient } from "@/aws/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import {
  runTypographyQa,
  type FormatKind,
  type TypographyQaResult,
  type TypographySizes,
} from "@/features/design/typography-qa";
import { TypographyQaDialog } from "@/components/design/typography-qa-dialog";
import { Progress } from "@/components/ui/progress";
import {
  uploadChatFile,
  retryExtraction,
  MAX_FILE_BYTES,
  MAX_FILES_PER_MESSAGE,
  type UploadedAttachment,
} from "@/lib/upload";
import { AttachmentChips, type ChipItem } from "@/components/chat/attachment-chips";

export const Route = createFileRoute("/design/$designId")({
  head: () => ({
    meta: [{ title: "Design — AskBell" }],
  }),
  component: DesignWorkspacePage,
});



// Must match PREVIEW_RENDERER_VERSION in awsClient/functions/design-agent/index.ts.
// When this string changes, all stored design.current_html values become stale
// and the route will force a server recomposition on next load.
const PREVIEW_RENDERER_VERSION = "2026-09-17-preview-v9-brand-fonts";

function htmlMatchesRenderer(html: string | null | undefined): boolean {
  if (!html) return false;
  return html.includes(`content="${PREVIEW_RENDERER_VERSION}"`)
    || html.includes(`data-renderer-version="${PREVIEW_RENDERER_VERSION}"`);
}

type DesignMessage = {
  id: string;
  role: "user" | "assistant" | "system" | "tool";
  content: string;
  events: any | null;
  created_at: string;
};

type Design = {
  id: string;
  user_id: string;
  title: string;
  brief: string | null;
  design_system_id: string | null;
  current_html: string | null;
  status: string;
};

type SystemOption = { id: string; name: string };

function DesignWorkspacePage() {
  const { designId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [design, setDesign] = useState<Design | null>(null);
  const [messages, setMessages] = useState<DesignMessage[]>([]);
  const [systems, setSystems] = useState<SystemOption[]>([]);
  const [input, setInput] = useState("");
  const [mobilePane, setMobilePane] = useState<"chat" | "workspace">("workspace");
  const [streaming, setStreaming] = useState(false);
  const [streamStatus, setStreamStatus] = useState<string | null>(null);
  const exportLock = useRef(false);
  const [checkingExport, setCheckingExport] = useState(false);
  const pptxRequest = useRef<{signature: string; id: string} | null>(null);
  const [exporting, setExporting] = useState<"pptx" | "pdf" | null>(null);
  const [pptxProgress, setPptxProgress] = useState<{
    phase: "preparing" | "rendering" | "uploading" | "finalizing" | "ready" | "downloading";
    label: string;
    percent: number;
  } | null>(null);
  const [smart, setSmart] = useState(false);
  const [rightTab, setRightTab] = useState<"preview" | "files" | "inputs" | "assets">("preview");
  const [qa, setQa] = useState<{
    result: TypographyQaResult;
    proceed: () => Promise<void> | void;
  } | null>(null);
  const [exportError, setExportError] = useState<{
    format: "pdf" | "pptx";
    step: "typography QA" | "fetch typography" | "HTML render" | "PDF rendering" | "agent export";
    message: string;
  } | null>(null);
  const [attachmentWarning, setAttachmentWarning] = useState<{
    reason: "link_query_failed" | "lookup_failed" | "all_orphaned" | "partial_orphaned";
    uploaded_count: number;
    loaded_count: number;
    missing_count: number;
    detail?: string;
  } | null>(null);
  const previewRef = useRef<HTMLIFrameElement>(null);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Attachments tied to this design (persisted via design_attachments)
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  // Transient chips for in-flight / failed uploads (not yet persisted)
  const [pendingChips, setPendingChips] = useState<ChipItem[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);

  const designWatch = useRef<AbortController | null>(null);
  const [activeJobId,setActiveJobId]=useState<string|null>(null);
  const [cancelling,setCancelling]=useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let reconnected=false;
    designWatch.current = controller;
    void (async () => {
      try {
        const state = await operation("design_turn_state", { designId });
        if (controller.signal.aborted || !state.jobId || !["queued", "running", "waiting"].includes(state.status)) return;
        setStreaming(true);
        reconnected=true;
        setStreamStatus("Reconnecting to Design…");
        await followDesignTurn(state.jobId, controller.signal);
        if (controller.signal.aborted) return;
        const [saved, history] = await Promise.all([
          awsClient.from("designs").select("*").eq("id", designId).single(),
          awsClient.from("design_messages").select("id,role,content,events,created_at").eq("design_id", designId).order("created_at"),
        ]);
        if (saved.data) setDesign(saved.data as Design);
        if (history.data) setMessages(history.data as DesignMessage[]);
      } catch (error) {
        if (!controller.signal.aborted) toast.error("Design connection interrupted", { description: error instanceof Error ? error.message : "Refresh to reconnect." });
      } finally {
        if (reconnected && !controller.signal.aborted) { setStreaming(false); setStreamStatus(null); }
      }
    })();
    return () => controller.abort();
  }, [designId]);

  // Initial load
  useEffect(() => {
    let active = true;
    const load = async () => {
      const [{ data: d }, { data: msgs }, { data: ss }, { data: das }] = await Promise.all([
        awsClient.from("designs").select("*").eq("id", designId).maybeSingle(),
        awsClient
          .from("design_messages")
          .select("id, role, content, events, created_at")
          .eq("design_id", designId)
          .order("created_at"),
        awsClient.from("design_systems").select("id, name").order("name"),
        awsClient
          .from("design_attachments")
          .select(
            "attachment_id, attachments:attachment_id ( id, filename, mime_type, size, storage_path, extracted_text )",
          )
          .eq("design_id", designId),
      ]);
      if (!active) return;
      if (!d) {
        toast.error("Design not found");
        navigate({ to: "/design" });
        return;
      }
      setDesign(d as Design);
      setMessages((msgs ?? []) as DesignMessage[]);
      setSystems((ss ?? []) as SystemOption[]);
      const linked: UploadedAttachment[] = ((das ?? []) as any[])
        .map((row) => row.attachments)
        .filter(Boolean)
        .map((a: any) => ({
          id: a.id,
          filename: a.filename,
          mime_type: a.mime_type,
          size: a.size,
          storage_path: a.storage_path,
          extracted_text: a.extracted_text ?? undefined,
        }));
      setAttachments(linked);

      // Self-heal stale composed HTML. If the stored current_html was produced
      // by an older renderer (no version marker, or a different one), ask the
      // backend to recompose with the current renderer before we render the
      // iframe. Skips when there's no html yet (deck not generated).
      const storedHtml = (d as Design).current_html;
      if (storedHtml && storedHtml.length > 100) {
        try {
          const json = await operation("design_rebuild", { designId: (d as Design).id });
          if (active && typeof json.html === "string") {
            setDesign((prev) => (prev ? { ...prev, current_html: json.html } : prev));
          }
        } catch (err) {
          if(active)toast.error("Preview could not refresh", {description:err instanceof Error?err.message:"Reload to retry."});
        }
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [designId, navigate]);

  // Upload + link files to this design
  const onPickFiles = () => fileInputRef.current?.click();

  const handleFiles = async (files: FileList | File[]) => {
    if (!user || !design) return;
    const arr = Array.from(files);
    const remaining = MAX_FILES_PER_MESSAGE - attachments.length - pendingChips.length;
    if (remaining <= 0) {
      toast.error(`Max ${MAX_FILES_PER_MESSAGE} files`);
      return;
    }
    const accepted = arr.slice(0, remaining).filter((f) => {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`${f.name} is too large (max 25 MB)`);
        return false;
      }
      return true;
    });
    for (const file of accepted) {
      const chipId = crypto.randomUUID();
      setPendingChips((prev) => [
        ...prev,
        { id: chipId, name: file.name, size: file.size, type: file.type, uploading: true },
      ]);
      try {
        const att = await uploadChatFile(file, user.id, null, { route: "design-asset", scopeId: design.id });
        // Link to this design
        if (att.id) {
          await operation("design_attach", { designId: design.id, attachmentId: att.id });
        }
        setAttachments((prev) => [...prev, att]);
        // Keep a warning chip if extraction failed so the user can retry.
        if (att.extraction_attempted && att.extraction_error) {
          setPendingChips((prev) =>
            prev.map((p) =>
              p.id === chipId
                ? {
                    ...p,
                    uploading: false,
                    extractionError: att.extraction_error ?? "Couldn't read file",
                    // Stash attachment id so retry can find it
                    id: att.id ?? chipId,
                  }
                : p,
            ),
          );
        } else {
          setPendingChips((prev) => prev.filter((p) => p.id !== chipId));
        }
      } catch (err: any) {
        setPendingChips((prev) =>
          prev.map((p) =>
            p.id === chipId
              ? { ...p, uploading: false, error: err?.message ?? "Upload failed" }
              : p,
          ),
        );
        toast.error(`Couldn't upload ${file.name}`, { description: err?.message });
      }
    }
  };

  const onRemoveAttachment = async (id: string) => {
    const att = attachments.find((a) => a.id === id);
    if (!att?.id || !design) {
      setPendingChips((prev) => prev.filter((p) => p.id !== id));
      return;
    }
    setAttachments((prev) => prev.filter((a) => a.id !== id));
    setPendingChips((prev) => prev.filter((p) => p.id !== id));
    try {
      await operation("design_detach", { designId: design.id, attachmentId: att.id });
    } catch (error) {
      setAttachments((prev) => [...prev, att]);
      toast.error("Couldn't remove attachment", { description: error instanceof Error ? error.message : "Please try again." });
    }
  };

  const onRetryExtraction = async (id: string) => {
    const att = attachments.find((a) => a.id === id);
    if (!att) return;
    setPendingChips((prev) =>
      prev.map((p) => (p.id === id ? { ...p, retrying: true } : p)),
    );
    const updated = await retryExtraction(att);
    setAttachments((prev) => prev.map((a) => (a.id === id ? updated : a)));
    if (updated.extraction_error) {
      setPendingChips((prev) =>
        prev.map((p) =>
          p.id === id
            ? { ...p, retrying: false, extractionError: updated.extraction_error ?? "Couldn't read file" }
            : p,
        ),
      );
    } else {
      setPendingChips((prev) => prev.filter((p) => p.id !== id));
      toast.success(`Read "${att.filename}" successfully.`);
    }
  };

  // Drag-and-drop on the chat panel
  const handleDragEnter = (e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length) void handleFiles(files);
  };

  // Scroll chat to bottom on new messages
  useEffect(() => {
    chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, streamStatus]);

  const updateSystem = async (next: string) => {
    if (!design) return;
    const value = next === "none" ? null : next;
    try {
      const result = await operation("design_set_system", { designId: design.id, systemId: value });
      setDesign({ ...design, design_system_id: value, current_html: result.html });
    } catch (error) {
      toast.error("Couldn't change design system", { description: error instanceof Error ? error.message : "Please try again." });
    }
    toast.success("Design system updated");
  };

  const uploadingCount = pendingChips.filter((p) => p.uploading).length;
  const hasUploadingFiles = uploadingCount > 0;

  const onSend = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!design || !user || !input.trim() || streaming) return;
    if (hasUploadingFiles) {
      toast.info(`Hang on — ${uploadingCount} file${uploadingCount === 1 ? "" : "s"} still uploading…`);
      return;
    }
    const userMsg = input.trim();
    setInput("");
    setStreaming(true);
    setStreamStatus("Sending…");
    setAttachmentWarning(null); // clear any previous attachment warning when a new turn starts

    // Optimistically add user message
    const tempId = crypto.randomUUID();
    setMessages((prev) => [
      ...prev,
      {
        id: tempId,
        role: "user",
        content: userMsg,
        events: null,
        created_at: new Date().toISOString(),
      },
    ]);

    try {
      const accepted = await operation("design_start", {
        requestId: tempId, designId: design.id, message: userMsg, smart,
        attachmentIds: attachments.map((a) => a.id).filter(Boolean),
      });
      await followDesignTurn(accepted.jobId, designWatch.current?.signal);

    } catch (err: any) {
      const message = err?.message || String(err);
      toast.error("Agent failed", { description: message });
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `❌ ${message}`,
          events: { type: "error" },
          created_at: new Date().toISOString(),
        },
      ]);
    } finally {
      setStreaming(false);
      setStreamStatus(null);
      // Reload current_html in case the stream finished after our last event
      const { data: d } = await awsClient.from("designs").select("*").eq("id", designId).maybeSingle();
      if (d) setDesign(d as Design);
      const { data: msgs } = await awsClient
        .from("design_messages")
        .select("id, role, content, events, created_at")
        .eq("design_id", designId)
        .order("created_at");
      setMessages((msgs ?? []) as DesignMessage[]);
    }
  };

  async function followDesignTurn(jobId: string, signal?: AbortSignal) {
    setActiveJobId(jobId);setCancelling(false);
    let after = 0;
    for (;;) {
      if (signal?.aborted) return;
      const state = await operation("design_turn_state", { designId, jobId, after });
      for (const event of state.events) {
        after = Math.max(after, event.sequence);
        handleEvent(event.data);
      }
      if (["succeeded", "failed", "partial", "cancelled"].includes(state.status)) {
        if (state.status !== "succeeded") throw new Error(state.error || `Design turn ${state.status}. Saved files are retained.`);
        return;
      }
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
  }

  const handleEvent = (evt: any) => {
    if (!evt || typeof evt !== "object") return;
    if (evt.type === "status") {
      setStreamStatus(evt.message || evt.step);
    } else if (evt.type === "clarify") {
      setStreamStatus("Asking clarifying questions…");
    } else if (evt.type === "plan") {
      setStreamStatus(`Planned ${evt.sections?.length || 0} sections…`);
    } else if (evt.type === "html_full") {
      setDesign((prev) => (prev ? { ...prev, current_html: evt.html, status: "ready" } : prev));
      setStreamStatus("Drafted deck.");
      setRightTab("preview");
    } else if (evt.type === "assistant_delta") {
      setStreamStatus("Designer is writing…");
    } else if (evt.type === "tool_start") {
      setStreamStatus(`${evt.role === "director" ? "🎨 Director" : "🧑‍🎨 Designer"}: ${evt.tool}…`);
    } else if (evt.type === "tool_result") {
      setStreamStatus(null);
    } else if (evt.type === "file_written") {
      setStreamStatus(`✏️ Wrote ${evt.path}`);
    } else if (evt.type === "image_generated") {
      setStreamStatus(`🖼️ Generated images/${evt.path.split("/").pop()}`);
    } else if (evt.type === "index_updated") {
      setStreamStatus(`📑 Updated deck order (${evt.paths?.length ?? 0} files)`);
    } else if (evt.type === "director_critique") {
      toast.info(`🎨 Director revised ${evt.file}`, {
        description: (evt.issues || []).slice(0, 2).join(" · "),
      });
    } else if (evt.type === "director_approved") {
      // silent — Director approval doesn't need a toast
    } else if (evt.type === "attachment_warning") {
      setAttachmentWarning({
        reason: evt.reason,
        uploaded_count: evt.uploaded_count ?? 0,
        loaded_count: evt.loaded_count ?? 0,
        missing_count: evt.missing_count ?? 0,
        detail: evt.detail,
      });
    } else if (evt.type === "error") {
      toast.error("Agent error", { description: evt.message });
    } else if (evt.type === "done") {
      setStreamStatus(null);
    }
  };

  // Fetch typography overrides for the active design system, then run QA.
  const fetchOverrides = async (
    fmt: FormatKind,
  ): Promise<TypographySizes | undefined> => {
    if (!design?.design_system_id) return undefined;
    const { data: ds, error } = await awsClient
      .from("design_systems")
      .select("fonts")
      .eq("id", design.design_system_id)
      .maybeSingle();
    if (error || !ds) throw new Error(error?.message || "The design system typography is unavailable.");
    const fonts = (ds.fonts ?? {}) as {
      sizes?: { pptx?: TypographySizes; pdf?: TypographySizes; html?: TypographySizes };
    };
    if (fmt === "pptx") return fonts.sizes?.pptx;
    if (fmt === "pdf") return fonts.sizes?.pdf;
    return fonts.sizes?.html ?? fonts.sizes?.pdf;
  };

  // Wraps an export action with the typography QA gate.
  const gateExport = async (fmt: FormatKind, proceed: () => Promise<void> | void) => {
    if (!design || exportLock.current) return;
    exportLock.current = true; setCheckingExport(true);
    let step: "fetch typography" | "typography QA" = "fetch typography";
    try {
      const overrides = await fetchOverrides(fmt);
      step = "typography QA";
      const result = runTypographyQa(fmt, overrides);
      const guarded = async () => { try { await proceed(); } finally { exportLock.current = false; } };
      if (!result.issues.some(issue => issue.severity === "error" || issue.severity === "warning")) await guarded();
      else setQa({result, proceed: guarded});
    } catch (error) {
      exportLock.current = false;
      setExportError({format: fmt === "pptx" ? "pptx" : "pdf", step, message: error instanceof Error ? error.message : String(error)});
    } finally { setCheckingExport(false); }
  };

  // Build a hidden iframe with the deliverable HTML, ready for rasterization.
  // Returns { frame, slides } so callers (PDF + PPTX) share the same path.
  const buildExportFrame = async (
    fmt: "pdf" | "pptx",
  ): Promise<{ frame: HTMLIFrameElement; slides: HTMLElement[] }> => {
    if (!design?.current_html) throw new Error("No deliverable to export.");
    const PDF_DEFAULTS = {
      headingPt: 24,
      bodyPt: 11,
      headingLineHeight: 1.2,
      bodyLineHeight: 1.55,
    };
    let typo = { ...PDF_DEFAULTS };
    let headingFont: string | undefined;
    let bodyFont: string | undefined;
    if (design.design_system_id) {
      const { data: ds, error } = await awsClient
        .from("design_systems")
        .select("fonts")
        .eq("id", design.design_system_id)
        .maybeSingle();
      if (error || !ds) throw new Error(error?.message || "The design system typography is unavailable.");
      const fonts = (ds.fonts ?? {}) as {
        heading?: string;
        body?: string;
        sizes?: { pdf?: Partial<typeof PDF_DEFAULTS>; pptx?: Partial<typeof PDF_DEFAULTS> };
      };
      headingFont = fonts.heading;
      bodyFont = fonts.body;
      const overrides = fmt === "pptx" ? fonts.sizes?.pptx : fonts.sizes?.pdf;
      typo = { ...PDF_DEFAULTS, ...(overrides || {}) };
    }

    const overrideCss = `
      :root {
        --doc-heading-pt: ${typo.headingPt}pt;
        --doc-body-pt: ${typo.bodyPt}pt;
        --doc-heading-lh: ${typo.headingLineHeight};
        --doc-body-lh: ${typo.bodyLineHeight};
        ${headingFont ? `--heading-font: ${headingFont}, serif;` : ""}
        ${bodyFont ? `--body-font: ${bodyFont}, sans-serif;` : ""}
      }
      html, body {
        margin: 0;
        padding: 0;
        background: transparent;
      }
      .pdf-export h1, .pdf-export h2, .pdf-export h3 {
        font-size: var(--doc-heading-pt);
        line-height: var(--doc-heading-lh);
        ${headingFont ? `font-family: ${headingFont}, serif;` : ""}
      }
      .pdf-export p, .pdf-export li, .pdf-export td {
        font-size: var(--doc-body-pt);
        line-height: var(--doc-body-lh);
        ${bodyFont ? `font-family: ${bodyFont}, sans-serif;` : ""}
      }
      /* Do NOT force .slide to 1920x1080 here — we measure the authored
         size first and scale to fit, so content always fills the frame. */
      .slide {
        box-sizing: border-box;
      }
    `;
    // html-to-image renders CSS in the browser through SVG foreignObject.
    // Preserve authored colors rather than rewriting them with a lossy parser.
    const sanitizedHtml = design.current_html;
    const hasFullDocument = /<html[\s>]/i.test(sanitizedHtml);
    const frameHtml = hasFullDocument
      ? sanitizedHtml.replace(/<\/head>/i, `<style>${overrideCss}</style></head>`)
      : `<!doctype html><html><head><meta charset="utf-8" /><style>${overrideCss}</style></head><body><div class="pdf-export">${sanitizedHtml}</div></body></html>`;

    const frame = document.createElement("iframe");
    frame.style.position = "fixed";
    frame.style.left = "-20000px";
    frame.style.top = "0";
    frame.style.width = "1920px";
    frame.style.height = "1080px";
    frame.style.border = "0";
    frame.setAttribute("aria-hidden", "true");
    await loadDesignExportFrame(frame, frameHtml);

    const exportDoc = frame.contentDocument;
    const exportRoot = exportDoc?.body;
    if (!exportDoc || !exportRoot) {
      frame.remove();
      throw new Error("Failed to prepare isolated export document.");
    }
    if ("fonts" in exportDoc && (exportDoc as any).fonts?.ready) {
      try {
        await (exportDoc as any).fonts.ready;
      } catch {
        /* font load best-effort */
      }
    }

    // Resolve slide-level backgrounds from data attributes if inline style is missing.
    const slides = Array.from(exportRoot.querySelectorAll<HTMLElement>(".slide"));
    for (const slide of slides) {
      const computed = exportDoc.defaultView?.getComputedStyle(slide);
      const hasBgImage = computed?.backgroundImage && computed.backgroundImage !== "none";
      const hasBgColor =
        computed?.backgroundColor &&
        computed.backgroundColor !== "rgba(0, 0, 0, 0)" &&
        computed.backgroundColor !== "transparent";
      if (!hasBgImage && !hasBgColor) {
        const bgAttr = slide.getAttribute("data-slide-bg");
        if (bgAttr) slide.style.background = bgAttr;
      }
    }
    return { frame, slides };
  };

  // Wrap a slide in a 1920x1080 stage and scale its authored content to fit.
  // This guarantees the captured image fills the slide regardless of the
  // natural authored dimensions of the markup.
  const renderSlideToStage = (
    slide: HTMLElement,
    doc: Document,
    bg?: string,
  ): { stage: HTMLElement; cleanup: () => void } => {
    const rect = slide.getBoundingClientRect();
    const naturalW = Math.max(slide.scrollWidth, Math.round(rect.width)) || 1920;
    const naturalH = Math.max(slide.scrollHeight, Math.round(rect.height)) || 1080;
    const scale = Math.min(1920 / naturalW, 1080 / naturalH);
    const scaledW = naturalW * scale;
    const scaledH = naturalH * scale;
    const offsetX = Math.max(0, (1920 - scaledW) / 2);
    const offsetY = Math.max(0, (1080 - scaledH) / 2);

    const stage = doc.createElement("div");
    stage.style.position = "fixed";
    stage.style.left = "0";
    stage.style.top = "0";
    stage.style.width = "1920px";
    stage.style.height = "1080px";
    stage.style.overflow = "hidden";
    stage.style.boxSizing = "border-box";
    stage.style.paddingLeft = `${offsetX}px`;
    stage.style.paddingTop = `${offsetY}px`;
    if (bg) stage.style.background = bg;

    const inner = doc.createElement("div");
    inner.style.width = `${naturalW}px`;
    inner.style.height = `${naturalH}px`;
    inner.style.transformOrigin = "top left";
    inner.style.transform = `scale(${scale})`;

    const originalParent = slide.parentNode;
    const originalNext = slide.nextSibling;
    inner.appendChild(slide);
    stage.appendChild(inner);
    doc.body.appendChild(stage);

    return {
      stage,
      cleanup: () => {
        if (originalParent) {
          if (originalNext) originalParent.insertBefore(slide, originalNext);
          else originalParent.appendChild(slide);
        }
        if (stage.parentNode) stage.parentNode.removeChild(stage);
      },
    };
  };



  const onExportPptx = async () => {
    if (!design?.current_html || exporting) return;
    setExportError(null);
    await gateExport("pptx", async () => {
      setExporting("pptx");
      setPptxProgress({ phase: "preparing", label: "Preparing deck…", percent: 2 });
      const step = "agent export" as const;
      try {
        const { data: { session } } = await awsClient.auth.getSession();
        const token = session?.csrfToken;
        if (!token) throw new Error("Not signed in.");

        const signature = JSON.stringify({designId: design.id, html: design.current_html, systemId: design.design_system_id});
        if (pptxRequest.current?.signature !== signature) pptxRequest.current = {signature, id: crypto.randomUUID()};
        const result = await operation<{designId:string;requestId:string},{url:string;filename:string}>(
          "design_export_pptx", { designId: design.id, requestId: pptxRequest.current.id }, token,
        );
        if (!result.url) throw new Error("Server did not return a download URL.");

        setPptxProgress({ phase: "downloading", label: "Downloading…", percent: 100 });
        const a = document.createElement("a");
        a.href = result.url;
        a.download = result.filename || `${design.title.replace(/[^a-z0-9-_]+/gi, "_")}.pptx`;
        a.target = "_blank";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        pptxRequest.current = null;
        toast.success("PPTX download started");
      } catch (err: any) {
        const message = err?.message ?? String(err);
        setExportError({ format: "pptx", step, message });
        toast.error("Couldn't export PPTX", { description: `${step}: ${message}` });
      } finally {
        setExporting(null);
        setTimeout(() => setPptxProgress(null), 600);
      }
    });
  };

  const onExportPdf = async () => {
    if (!design?.current_html || exporting) return;
    setExportError(null);
    await gateExport("pdf", async () => {
      setExporting("pdf");
      let step: "fetch typography" | "HTML render" | "PDF rendering" = "HTML render";
      let frame: HTMLIFrameElement | null = null;
      try {
        const built = await buildExportFrame("pdf");
        frame = built.frame;
        const slides = built.slides;
        if (slides.length === 0) {
          throw new Error("No .slide elements found for PDF export.");
        }

        step = "PDF rendering";
        const { toJpeg } = await import("html-to-image");
        const { jsPDF } = await import("jspdf");

        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "px",
          format: [1920, 1080],
          compress: true,
        });

        const exportDoc = frame.contentDocument!;
        // html-to-image's font discovery uses the parent window's HTMLElement
        // constructor, so it misses descendants owned by this isolated iframe.
        // The preview already embeds the approved font files as data URLs.
        const fontEmbedCSS = Array.from(exportDoc.styleSheets).flatMap(sheet => Array.from(sheet.cssRules))
          .filter(rule => rule.type === 5).map(rule => rule.cssText).join("\n");
        for (const [index, slide] of slides.entries()) {
          // Pick a background color to fill any letterboxing — prefer the slide's
          // own background, fall back to white only if there's nothing at all.
          const computed = exportDoc.defaultView?.getComputedStyle(slide);
          const hasGradient =
            computed?.backgroundImage && computed.backgroundImage !== "none";
          const bgColor = hasGradient
            ? undefined
            : computed?.backgroundColor &&
                computed.backgroundColor !== "rgba(0, 0, 0, 0)" &&
                computed.backgroundColor !== "transparent"
              ? computed.backgroundColor
              : "#ffffff";
          const { stage, cleanup } = renderSlideToStage(slide, exportDoc, bgColor);
          try {
            const dataUrl = await toJpeg(stage, {
              quality: 0.96,
              fontEmbedCSS,
              pixelRatio: 1,
              width: 1920,
              height: 1080,
              backgroundColor: bgColor,
              cacheBust: true,
            });
            if (index > 0) pdf.addPage([1920, 1080], "landscape");
            pdf.addImage(dataUrl, "JPEG", 0, 0, 1920, 1080, undefined, "FAST");
          } finally {
            cleanup();
          }
        }

        pdf.save(`${design.title.replace(/[^a-z0-9-_]+/gi, "_")}.pdf`);
        toast.success("PDF downloaded");
      } catch (err: any) {
        const message = err?.message ?? String(err);
        setExportError({ format: "pdf", step, message });
        toast.error("Couldn't export PDF", { description: `${step}: ${message}` });
      } finally {
        if (frame && frame.parentNode) frame.parentNode.removeChild(frame);
        setExporting(null);
      }
    });
  };



  if (!design) {
    return (
      <AppLayout>
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const hasHtml = !!design.current_html && design.current_html.length > 100;

  return (
    <AppLayout>
      <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2 shrink-0">
          <div className="flex items-center gap-2 min-w-0 w-full md:w-auto md:flex-1">
            <Button asChild variant="ghost" size="sm" className="-ml-2 gap-1.5 shrink-0">
              <Link to="/design">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <Palette className="h-4 w-4 text-primary shrink-0" />
            <span className="font-bold text-ui-base truncate">{design.title}</span>
            <Badge variant="outline" className="text-ui-xs uppercase tracking-wider shrink-0">
              {design.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setSmart((v) => !v)}
              className={`text-ui-xs px-2 py-1 rounded border transition-colors ${
                smart
                  ? "border-primary text-primary bg-primary/10"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
              title="Use a stronger model (slower, more expensive)"
            >
              {smart ? "Smart" : "Fast"}
            </button>
            <Button
              onClick={onExportPdf}
              disabled={!hasHtml || !!exporting || checkingExport || !!qa}
              size="sm"
              variant="outline"
              className="gap-1.5"
            >
              {exporting === "pdf" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              PDF
            </Button>
            <Button
              onClick={onExportPptx}
              disabled={!hasHtml || !!exporting || checkingExport || !!qa}
              size="sm"
              className="gap-1.5"
            >
              {exporting === "pptx" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileText className="h-3.5 w-3.5" />
              )}
              PPTX
            </Button>
          </div>
        </div>

        {exportError && (
          <div
            role="alert"
            className="border-b border-destructive/40 bg-destructive/10 px-4 py-2 text-ui-base text-destructive flex items-start gap-2 shrink-0"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold">
                {exportError.format.toUpperCase()} export failed at{" "}
                <span className="underline underline-offset-2">{exportError.step}</span>
              </div>
              <div className="text-ui-sm opacity-90 break-words mt-0.5 font-mono">
                {exportError.message}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setExportError(null)}
              className="opacity-70 hover:opacity-100 shrink-0"
              aria-label="Dismiss export error"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {pptxProgress && (
          <div
            role="status"
            aria-live="polite"
            className="border-b border-border bg-muted/40 px-4 py-2 shrink-0"
          >
            <div className="flex items-center gap-2 text-ui-base">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
              <span className="font-bold">Exporting PPTX</span>
              <span className="text-muted-foreground">— {pptxProgress.label}</span>
              <span className="ml-auto text-ui-sm tabular-nums text-muted-foreground">
                {pptxProgress.percent}%
              </span>
            </div>
            <Progress value={pptxProgress.percent} className="h-1.5 mt-1.5" />
          </div>
        )}

        <div className="md:hidden flex gap-1 border-b border-border bg-muted/20 p-2" aria-label="Design workspace view">
          <Button variant={mobilePane === "chat" ? "secondary" : "ghost"} size="sm" className="flex-1" aria-pressed={mobilePane === "chat"} onClick={() => setMobilePane("chat")}>Chat</Button>
          <Button variant={mobilePane === "workspace" ? "secondary" : "ghost"} size="sm" className="flex-1" aria-pressed={mobilePane === "workspace"} onClick={() => setMobilePane("workspace")}>Workspace</Button>
        </div>
        <ResizablePanelGroup orientation="horizontal" data-mobile-pane={mobilePane} className="design-workspace-panels flex-1 min-h-0 min-w-0">
          {/* LEFT: Chat */}
          <ResizablePanel defaultSize={40} minSize={28} className="min-w-0">
            <div
              className="h-full flex flex-col relative"
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Design system picker */}
              <div className="px-4 py-2 border-b border-border shrink-0 flex items-center gap-2">
                <span className="text-ui-xs text-muted-foreground uppercase tracking-wider">
                  System
                </span>
                <Select
                  value={design.design_system_id ?? "none"}
                  onValueChange={updateSystem}
                >
                  <SelectTrigger className="h-7 text-ui-sm flex-1 min-w-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (default)</SelectItem>
                    {systems.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button asChild size="sm" variant="ghost" className="h-7 text-ui-sm">
                  <Link to="/design/systems">Manage</Link>
                </Button>
              </div>

              {/* Messages */}
              <div ref={chatScrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center text-muted-foreground py-8">
                    <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-ui-base">
                      Tell the agent what to build. It will plan, draft, and refine.
                    </p>
                    <p className="text-ui-sm mt-2 opacity-70">
                      Drop PDF, DOCX, PPTX, XLSX, CSV, MD, or TXT files to give it source material.
                    </p>
                    {design.brief && (
                      <p className="mt-3 text-ui-sm italic max-w-sm mx-auto">"{design.brief}"</p>
                    )}
                  </div>
                )}

                {attachmentWarning && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-ui-sm border border-destructive/40 bg-destructive/10 p-3 text-ui-base"
                  >
                    <AlertCircle className="h-4 w-4 mt-0.5 text-destructive shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-destructive">
                        {attachmentWarning.reason === "link_query_failed"
                          ? "Couldn't check your uploaded files"
                          : attachmentWarning.reason === "all_orphaned"
                            ? `All ${attachmentWarning.uploaded_count} uploaded file${attachmentWarning.uploaded_count === 1 ? "" : "s"} couldn't be loaded`
                            : attachmentWarning.reason === "partial_orphaned"
                              ? `Loaded ${attachmentWarning.loaded_count} of ${attachmentWarning.uploaded_count} uploaded files — ${attachmentWarning.missing_count} missing`
                              : `Couldn't load ${attachmentWarning.uploaded_count} uploaded file${attachmentWarning.uploaded_count === 1 ? "" : "s"}`}
                      </p>
                      <p className="text-ui-sm text-muted-foreground mt-1">
                        The agent answered without your source material. Try removing and re-uploading the file{attachmentWarning.uploaded_count === 1 ? "" : "s"} from the input below.
                        {attachmentWarning.detail ? ` (${attachmentWarning.detail})` : null}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAttachmentWarning(null)}
                      className="text-muted-foreground hover:text-foreground shrink-0"
                      aria-label="Dismiss"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}

                {renderMessages(messages)}

                {streaming && streamStatus && (
                  <div className="flex items-center gap-2 text-ui-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {streamStatus}
                    {activeJobId&&<Button type="button" variant="outline" size="sm" disabled={cancelling} onClick={async()=>{setCancelling(true);try{await operation("design_cancel",{designId,jobId:activeJobId});setStreamStatus("Stopping after the current action…");}catch(error){setCancelling(false);toast.error(error instanceof Error?error.message:"Could not stop Design.");}}}>{cancelling?"Stopping…":"Stop"}</Button>}
                  </div>
                )}
              </div>

              {/* Input */}
              <form
                onSubmit={onSend}
                className="border-t border-border p-3 shrink-0"
              >
                {(attachments.length > 0 || pendingChips.length > 0) && (
                  <AttachmentChips
                    items={[
                      ...attachments
                        .filter((a) => !pendingChips.some((p) => p.id === a.id))
                        .map((a) => ({
                          id: a.id ?? a.storage_path,
                          name: a.filename,
                          size: a.size,
                          type: a.mime_type,
                        })),
                      ...pendingChips,
                    ]}
                    onRemove={onRemoveAttachment}
                    onRetryExtraction={onRetryExtraction}
                  />
                )}
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.tsv,.txt,.md,.markdown,.json,.html,.htm,.rtf,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/*,image/*"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files) void handleFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={onPickFiles}
                    disabled={streaming}
                    title="Attach files"
                    className="shrink-0"
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Textarea
                    aria-label="Message the Design agent"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.altKey || e.ctrlKey)) {
                        e.preventDefault();
                        const ta = e.currentTarget;
                        const start = ta.selectionStart ?? ta.value.length;
                        const end = ta.selectionEnd ?? ta.value.length;
                        const next = ta.value.slice(0, start) + "\n" + ta.value.slice(end);
                        setInput(next);
                        requestAnimationFrame(() => {
                          ta.selectionStart = ta.selectionEnd = start + 1;
                        });
                        return;
                      }
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void onSend();
                      }
                    }}
                    onPaste={(e) => {
                      // 1) Pasted files (e.g. screenshots) → attach as upload.
                      const cbFiles = e.clipboardData.files;
                      if (cbFiles && cbFiles.length > 0) {
                        e.preventDefault();
                        const files = Array.from(cbFiles).map((f) => {
                          if (!f.name || /^image\.(png|jpe?g|webp|gif)$/i.test(f.name)) {
                            const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
                            const ts = new Date().toISOString().replace(/[:T]/g, "-").replace(/\..+$/, "");
                            return new File([f], `pasted-${ts}.${ext}`, { type: f.type });
                          }
                          return f;
                        });
                        void handleFiles(files);
                        toast.success(`Pasted ${files.length} file${files.length === 1 ? "" : "s"}`);
                        return;
                      }
                      // 2) Pasted long text (>500 chars) → save as .txt attachment.
                      const PASTE_INLINE_MAX = 500;
                      const text = e.clipboardData.getData("text/plain");
                      if (!text || text.length <= PASTE_INLINE_MAX) return;
                      e.preventDefault();
                      const ts = new Date()
                        .toISOString()
                        .replace(/[:T]/g, "-")
                        .replace(/\..+$/, "");
                      const filename = `pasted-${ts}.txt`;
                      const file = new File([text], filename, { type: "text/plain" });
                      void handleFiles([file]);
                      toast.success(`Pasted text saved as ${filename}`, {
                        description: `${text.length.toLocaleString()} characters attached as a file.`,
                      });
                    }}
                    placeholder={
                      hasHtml
                        ? "Tell me what to tweak — 'make slide 3 punchier', 'add a pricing slide'…"
                        : attachments.length > 0
                          ? "Tell me what to design from these files…"
                          : "Attach source files and describe the deck or document you want…"
                    }
                    className="min-h-[60px] max-h-[160px] resize-none"
                    disabled={streaming}
                  />
                  <Button
                    type="submit"
                    aria-label="Send Design message"
                    size="icon"
                    disabled={!input.trim() || streaming || hasUploadingFiles}
                    title={hasUploadingFiles ? `Waiting for ${uploadingCount} upload${uploadingCount === 1 ? "" : "s"}…` : undefined}
                  >
                    {streaming || hasUploadingFiles ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </form>

              {isDragging && (
                <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none bg-primary/10 backdrop-blur-sm ring-2 ring-primary ring-inset">
                  <div className="rounded-ui-md border-2 border-dashed border-primary px-6 py-4 bg-background/90 text-primary text-ui-base font-bold  flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Drop files to attach
                  </div>
                </div>
              )}
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle className="max-md:hidden" />

          {/* RIGHT: Preview / Inputs / Assets */}
          <ResizablePanel defaultSize={60} minSize={30} className="min-w-0">
            <Tabs
              value={rightTab}
              onValueChange={(v) => setRightTab(v as typeof rightTab)}
              className="h-full flex flex-col"
            >
              <div className="border-b border-border px-3 py-1.5 shrink-0 flex flex-wrap items-center gap-2">
                <TabsList className="h-7 p-0.5">
                  <TabsTrigger value="preview" className="text-ui-sm h-6 px-2.5">
                    Preview
                  </TabsTrigger>
                  <TabsTrigger value="files" className="text-ui-sm h-6 px-2.5 gap-1">
                    <FolderTree className="h-3 w-3" />
                    Files
                  </TabsTrigger>
                  <TabsTrigger value="inputs" className="text-ui-sm h-6 px-2.5">
                    Inputs{attachments.length > 0 && ` · ${attachments.length}`}
                  </TabsTrigger>
                  <TabsTrigger value="assets" className="text-ui-sm h-6 px-2.5">
                    Assets
                  </TabsTrigger>
                </TabsList>
                {rightTab === "preview" && hasHtml && (
                  <button
                    type="button"
                    onClick={() => {
                      const html = design?.current_html ?? "";
                      if (!html) return;
                      const blob = new Blob([isolatedDesignWindow(html)], { type: "text/html" });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url; link.target = "_blank"; link.rel = "noopener noreferrer";
                      link.click();
                      // Revoke after the new tab has had time to load.
                      setTimeout(() => URL.revokeObjectURL(url), 60_000);
                    }}
                    className="ml-auto text-ui-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors"
                    title="Open the current preview in a new browser window for full-screen viewing"
                  >
                    <ExternalLink className="h-3 w-3" />
                    Open in window
                  </button>
                )}
              </div>

              <TabsContent value="preview" className="flex-1 min-h-0 m-0 bg-muted/30 flex flex-col">
                {hasHtml ? (
                  <iframe
                    ref={previewRef}
                    title="Design preview"
                    srcDoc={design.current_html ?? ""}
                    sandbox="allow-same-origin"
                    className="flex-1 w-full bg-ui-surface"
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-muted-foreground p-8 text-center">
                    <div>
                      <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                      <p className="text-ui-base font-bold text-foreground mb-1">
                        Preview will appear here
                      </p>
                      <p className="text-ui-sm max-w-sm mx-auto">
                        Send a message to start. The agent will plan the deck and draft
                        the HTML — you'll see it render here in real time.
                      </p>
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="files" className="flex-1 min-h-0 m-0 flex flex-col">
                <FilesPanel
                  designId={design.id}
                  onChanged={async () => {
                    const { data: d } = await awsClient
                      .from("designs")
                      .select("*")
                      .eq("id", designId)
                      .maybeSingle();
                    if (d) setDesign(d as Design);
                  }}
                />
              </TabsContent>

              <TabsContent value="inputs" className="flex-1 min-h-0 m-0 flex flex-col">
                <InputsPanel attachments={attachments} />
              </TabsContent>

              <TabsContent value="assets" className="flex-1 min-h-0 m-0 flex flex-col">
                {user && <AssetsPanel designId={design.id} userId={user.id} />}
              </TabsContent>
            </Tabs>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
      <TypographyQaDialog
        open={!!qa}
        result={qa?.result ?? null}
        systemId={design.design_system_id}
        busy={!!exporting}
        onCancel={() => { exportLock.current = false; setQa(null); }}
        onConfirm={async () => {
          const action = qa?.proceed;
          setQa(null);
          if (action) await action();
        }}
      />
    </AppLayout>
  );
}

// Group raw design_messages into renderable items:
// - user / plain assistant text → bubble
// - assistant w/ tool_calls + following tool results → collapsible "Designer used X" card
// - director_critique events → director note bubble
// - role: "tool" rows are NEVER rendered standalone (they live inside their parent card)
type RenderItem =
  | { kind: "user" | "assistant"; id: string; content: string; isError?: boolean }
  | { kind: "director"; id: string; file: string; issues: string[]; suggested_fix: string }
  | {
      kind: "tools";
      id: string;
      calls: Array<{
        id: string;
        name: string;
        args: any;
        result: any | null;
      }>;
      preface: string;
    };

function summarizeToolArgs(name: string, args: any): string {
  if (!args || typeof args !== "object") return "";
  if (name === "read_input_file") return String(args.filename ?? "");
  if (name === "read_file" || name === "delete_file") return String(args.path ?? "");
  if (name === "write_file") return String(args.path ?? "");
  if (name === "web_search") return String(args.query ?? "").slice(0, 80);
  if (name === "set_index") return `${(args.paths ?? []).length} files`;
  if (name === "plan_images") return `${(args.slots ?? []).length} slots`;
  if (name === "generate_image") return String(args.slot_id ?? "");
  if (name === "assign_image_to_slot") return String(args.slot_id ?? "");
  if (name === "request_clarification") return `${(args.questions ?? []).length} question(s)`;
  if (name === "run_skill_script") return `${args.skill_id ?? ""}/${args.script_path ?? ""}`;
  return "";
}

function renderMessages(messages: DesignMessage[]) {
  const items: RenderItem[] = [];
  // Index tool messages by tool_call_id for fast lookup
  const toolByCallId = new Map<string, DesignMessage>();
  for (const m of messages) {
    if (m.role === "tool" && m.events?.tool_call_id) {
      toolByCallId.set(m.events.tool_call_id, m);
    }
  }

  for (const m of messages) {
    if (m.role === "user") {
      items.push({ kind: "user", id: m.id, content: m.content });
      continue;
    }
    if (m.role === "tool") continue; // rendered inside its parent card
    if (m.role !== "assistant") continue;

    const evt = m.events ?? {};
    if (evt.type === "director_critique") {
      items.push({
        kind: "director",
        id: m.id,
        file: evt.file ?? "",
        issues: Array.isArray(evt.issues) ? evt.issues : [],
        suggested_fix: evt.suggested_fix ?? "",
      });
      continue;
    }

    if (evt.type === "tool_call_batch" && Array.isArray(evt.tool_calls)) {
      const calls = evt.tool_calls.map((tc: any) => {
        let parsedArgs: any = {};
        try {
          parsedArgs = JSON.parse(tc.function?.arguments || "{}");
        } catch {
          parsedArgs = {};
        }
        const linked = toolByCallId.get(tc.id);
        let parsedResult: any = null;
        if (linked) {
          try {
            parsedResult = JSON.parse(linked.content || "null");
          } catch {
            parsedResult = linked.content;
          }
        }
        return {
          id: tc.id,
          name: tc.function?.name ?? "tool",
          args: parsedArgs,
          result: parsedResult,
        };
      });
      items.push({
        kind: "tools",
        id: m.id,
        calls,
        preface: m.content?.trim() || "",
      });
      continue;
    }

    // Plain assistant text — only render if non-empty
    const isError = evt?.type === "error";
    if (m.content?.trim() || isError) {
      items.push({ kind: "assistant", id: m.id, content: m.content, isError });
    }
  }

  return items.map((item) => {
    if (item.kind === "user" || item.kind === "assistant") {
      const isUser = item.kind === "user";
      return (
        <div key={item.id} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
          <div
            className={`min-w-0 max-w-[90%] [overflow-wrap:anywhere] rounded-ui-md px-3 py-2 text-ui-base ${
              isUser
                ? "bg-primary text-primary-foreground"
                : item.isError
                  ? "bg-destructive/10 text-destructive border border-destructive/30"
                  : "bg-secondary text-foreground"
            }`}
          >
            {item.isError && <AlertCircle className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />}
            {isUser ? (
              <div className="whitespace-pre-wrap leading-relaxed">{item.content}</div>
            ) : (
              <div className="[&_pre]:overflow-x-auto [&_pre]:max-w-full [&_table]:block [&_table]:overflow-x-auto leading-relaxed prose prose-sm prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0 prose-headings:my-2 prose-pre:my-2 prose-pre:bg-muted/50 prose-code:text-foreground prose-a:text-primary">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {item.content || ""}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      );
    }
    if (item.kind === "director") {
      return (
        <div key={item.id} className="flex justify-start">
          <div className="max-w-[90%] rounded-ui-md px-3 py-2 text-ui-sm border border-primary/30 bg-primary/5 text-foreground">
            <div className="font-bold mb-1">🎨 Director revised {item.file}</div>
            {item.issues.length > 0 && (
              <ul className="list-disc ml-4 space-y-0.5 opacity-90">
                {item.issues.slice(0, 4).map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            )}
            {item.suggested_fix && (
              <div className="mt-1.5 italic opacity-80">→ {item.suggested_fix}</div>
            )}
          </div>
        </div>
      );
    }
    if (item.kind === "tools") {
      return <ToolBatchCard key={item.id} item={item} />;
    }
    return null;
  });
}

function ToolBatchCard({
  item,
}: {
  item: Extract<RenderItem, { kind: "tools" }>;
}) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] w-full rounded-ui-md border border-border bg-muted/30 text-ui-sm">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-1.5 px-2.5 py-1.5 text-left hover:bg-muted/60 rounded-t-ui-md"
        >
          <ChevronRight
            className={`h-3 w-3 shrink-0 transition-transform ${open ? "rotate-90" : ""}`}
          />
          <Wrench className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">
            {item.calls.length === 1
              ? `Used ${item.calls[0].name}`
              : `Used ${item.calls.length} tools`}
          </span>
          <span className="text-muted-foreground/70 truncate">
            {item.calls
              .map((c) => {
                const s = summarizeToolArgs(c.name, c.args);
                return s ? `${c.name}(${s})` : c.name;
              })
              .join(" · ")}
          </span>
        </button>
        {open && (
          <div className="border-t border-border px-2.5 py-2 space-y-1.5">
            {item.preface && (
              <div className="whitespace-pre-wrap text-foreground/80 mb-1.5">{item.preface}</div>
            )}
            {item.calls.map((c) => {
              const isExpanded = expandedId === c.id;
              const argsStr = JSON.stringify(c.args, null, 2);
              const resultStr =
                c.result === null
                  ? "(no result)"
                  : typeof c.result === "string"
                    ? c.result
                    : JSON.stringify(c.result, null, 2);
              return (
                <div key={c.id} className="rounded border border-border bg-background/40">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : c.id)}
                    className="w-full flex items-center gap-1.5 px-2 py-1 text-left hover:bg-muted/40"
                  >
                    <ChevronRight
                      className={`h-3 w-3 shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                    />
                    <span className="font-mono font-bold">{c.name}</span>
                    <span className="text-muted-foreground/70 truncate">
                      {summarizeToolArgs(c.name, c.args)}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="px-2 pb-2 space-y-1.5">
                      <div>
                        <div className="text-ui-xs uppercase tracking-wider text-muted-foreground mb-0.5">
                          Args
                        </div>
                        <pre className="text-ui-xs bg-muted/40 rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                          {argsStr}
                        </pre>
                      </div>
                      <div>
                        <div className="text-ui-xs uppercase tracking-wider text-muted-foreground mb-0.5">
                          Result
                        </div>
                        <pre className="text-ui-xs bg-muted/40 rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all max-h-60 overflow-y-auto">
                          {resultStr.slice(0, 4000)}
                          {resultStr.length > 4000 ? "\n…(truncated)" : ""}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
